import { Injectable } from '@angular/core';
import {
  AuditAction,
  AuditActionDescription,
  AuditActionType,
  DocumentMarkupPage,
  EtJournalEntry,
  EtJournalESeal,
  EtJournalSealSource,
  EtJournalSigner,
  SealEntryOps,
  ToSealsParams,
} from '@et/typings';

@Injectable({
  providedIn: 'root',
})
export class EsealsConverterService {
  /**
   * Builds page number → document name from markup `aiPageType.category`.
   * Skips empty categories and the sentinel "Unknown" so the UI can show its default label.
   */
  pageNamesFromMarkupPages(
    pages: Pick<DocumentMarkupPage, 'page' | 'aiPageType'>[] | undefined | null,
  ): Record<number, string> {
    const out: Record<number, string> = {};
    if (!pages?.length) {
      return out;
    }
    for (const page of pages) {
      const name = page.aiPageType?.category?.trim();
      if (name && name.toLowerCase() !== 'unknown') {
        out[page.page] = name;
      }
    }
    return out;
  }

  /**
   * Builds seal/eSeal cards from persisted entries and Notarized audit actions.
   * Groups by content fingerprint (date + metadata), not date alone, so distinct
   * manual seals can share a notarizationDate. Virtual audit cards fill gaps.
   */
  toSeals({
    entries,
    signers,
    notarizedActions,
    packetId,
    eJournalPacketId,
    pageNamesByNumber = {},
    auditActionsLoaded = false,
    defaultFee = 0,
  }: ToSealsParams): EtJournalESeal[] {
    const groups = new Map<string, EtJournalEntry[]>();

    for (const entry of entries) {
      if (entry.cancelActUtcDate || !entry.notarizationDate) {
        continue;
      }
      const key = this.sealGroupKey(entry);
      if (!key) {
        continue;
      }
      const group = groups.get(key);
      if (group) {
        group.push(entry);
      } else {
        groups.set(key, [entry]);
      }
    }

    const seals: EtJournalESeal[] = [];
    const coveredDates = new Set<string>();
    const params: ToSealsParams = {
      entries,
      signers,
      notarizedActions,
      packetId,
      eJournalPacketId,
      pageNamesByNumber,
      auditActionsLoaded,
      defaultFee,
    };

    for (const [, groupEntries] of groups) {
      const seal = this.entriesGroupToSeal(groupEntries, params);
      coveredDates.add(this.normalizeNotarizationDate(seal.notarizationDate));
      seals.push(seal);
    }

    for (const action of notarizedActions) {
      if (action.actionType !== AuditActionType.Notarized) {
        continue;
      }
      const dateKey = this.normalizeNotarizationDate(action.actionDateTime);
      if (!dateKey || coveredDates.has(dateKey)) {
        continue;
      }
      seals.push(this.auditActionToVirtualSeal(action, dateKey, params));
      coveredDates.add(dateKey);
    }

    // Same ordering as EjournalEntriesService.sortEntries: page number ascending
    // (seals without a page number come last), then notarized date ascending.
    // Cancellation acts never become seals (filtered above), so that rule is
    // already satisfied here.
    return seals.sort((a, b) => {
      const aHasPage = a.pageNumber != null;
      const bHasPage = b.pageNumber != null;
      if (aHasPage !== bHasPage) return aHasPage ? -1 : 1;
      if (aHasPage && bHasPage && a.pageNumber !== b.pageNumber) {
        return (a.pageNumber as number) - (b.pageNumber as number);
      }
      return a.notarizationDate.localeCompare(b.notarizationDate);
    });
  }

  /**
   * Diffs previous vs next seal into holder/signer create / patch / delete ops.
   */
  diffSealSave(
    previousSeal: EtJournalESeal | null,
    nextSeal: EtJournalESeal,
    signers: EtJournalSigner[],
  ): SealEntryOps {
    const prevSignerIds = new Set(previousSeal?.signerIds ?? []);
    const nextSignerIds = new Set(nextSeal.signerIds);
    const prevSignerEntries = previousSeal?.entries ?? [];
    const entryBySignerId = this.mapEntriesToSigners(
      prevSignerEntries,
      signers,
    );

    const createHolder = !previousSeal?.holderEntry;

    const create: SealEntryOps['create'] = [];
    for (const signerId of nextSignerIds) {
      if (!prevSignerIds.has(signerId)) {
        create.push({ signerId });
      }
    }

    const deleteOps: SealEntryOps['delete'] = [];
    for (const signerId of prevSignerIds) {
      if (!nextSignerIds.has(signerId)) {
        const entry = entryBySignerId.get(signerId);
        if (entry) {
          deleteOps.push({ entry, signerId });
        }
      }
    }

    const remainingSignerEntries = prevSignerEntries.filter(
      (e) =>
        !deleteOps.some((d) => d.entry.eJournalEntryId === e.eJournalEntryId),
    );

    const metadataChanged =
      !!previousSeal && this.sealMetadataChanged(previousSeal, nextSeal);

    const toPatch: EtJournalEntry[] = [];
    if (metadataChanged) {
      const applyMeta = (entry: EtJournalEntry): EtJournalEntry => ({
        ...entry,
        documentDescription: nextSeal.documentDescription,
        pageNumber: nextSeal.pageNumber,
        notorialAct: nextSeal.notorialAct,
        notarizationDate: nextSeal.notarizationDate,
        feeCharged: nextSeal.feeCharged,
        notes: nextSeal.notes,
      });
      if (previousSeal?.holderEntry) {
        toPatch.push(applyMeta(previousSeal.holderEntry));
      }
      toPatch.push(...remainingSignerEntries.map(applyMeta));
    }

    return { createHolder, create, patch: toPatch, delete: deleteOps };
  }

  /**
   * Builds a new unsaved manual seal shell for the add dialog.
   */
  createEmptySeal(
    packetId: string,
    eJournalPacketId: string,
    defaultFee = 0,
  ): EtJournalESeal {
    return {
      sealId: `temp-${crypto.randomUUID()}`,
      source: 'seal',
      documentDescription: '',
      pageNumber: undefined,
      notorialAct: '',
      notarizationDate: '',
      feeCharged: defaultFee,
      notes: undefined,
      utcCreatedOn: new Date().toISOString(),
      ipAddress: undefined,
      eJournalPacketId,
      packetId,
      holderEntry: undefined,
      entries: [],
      signerIds: [],
    };
  }

  /**
   * Normalizes a notarization datetime to a stable ISO string for display/matching.
   */
  normalizeNotarizationDate(value?: string | null): string {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value.trim();
    }
    return parsed.toISOString();
  }

  /**
   * Content fingerprint used to group holder + signer entries into one seal.
   * Includes notarizationDate so same-minute seals with different metadata stay separate.
   */
  sealGroupKey(entry: EtJournalEntry): string {
    const dateKey = this.normalizeNotarizationDate(entry.notarizationDate);
    if (!dateKey) {
      return '';
    }
    return [
      dateKey,
      entry.documentDescription ?? '',
      entry.notorialAct ?? '',
      entry.pageNumber ?? '',
      entry.notes ?? '',
    ].join('|');
  }

  signerHasEntry(signer: EtJournalSigner, entryId: string): boolean {
    return this.parseEntryIds(signer.ejournalEntryIds).includes(entryId);
  }

  findSignerIdForEntry(
    entry: EtJournalEntry,
    signers: EtJournalSigner[],
  ): string | null {
    const signer = signers.find((s) =>
      this.signerHasEntry(s, entry.eJournalEntryId),
    );
    return signer?.eJournalSignerId ?? null;
  }

  parseNotarizedDetail(action: AuditAction): AuditActionDescription | null {
    if (!action.detailDescription) {
      return null;
    }
    try {
      return JSON.parse(action.detailDescription) as AuditActionDescription;
    } catch {
      return null;
    }
  }

  /**
   * Returns Notarized audit actions that have not been superseded by a later erase
   * for the same entity and page.
   */
  getCleanNotarizedAuditActions(auditActions: AuditAction[]): AuditAction[] {
    const erases = auditActions
      .filter((a) => a.actionType === AuditActionType.Other)
      .map((a) => {
        const desc = this.parseAuditDescription(a.actionTypeOtherDescription);
        if (desc?.action !== 'erase' || desc.page == null) {
          return null;
        }
        return {
          entityId: a.entityId,
          page: desc.page,
          actionDateTime: a.actionDateTime ?? '',
        };
      })
      .filter(
        (e): e is { entityId: string; page: number; actionDateTime: string } =>
          e != null,
      );

    return auditActions.filter((a) => {
      if (a.actionType !== AuditActionType.Notarized) {
        return false;
      }
      const detail = this.parseAuditDescription(a.detailDescription);
      if (detail?.page == null) {
        return false;
      }
      const notarizedAt = a.actionDateTime ?? '';
      const wasErased = erases.some(
        (e) =>
          e.entityId === a.entityId &&
          e.page === detail.page &&
          e.actionDateTime > notarizedAt,
      );
      return !wasErased;
    });
  }

  private parseAuditDescription(
    raw: string | undefined,
  ): { page?: number; action?: string } | null {
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as { page?: number; action?: string };
    } catch {
      return null;
    }
  }

  private entriesGroupToSeal(
    groupEntries: EtJournalEntry[],
    {
      signers,
      notarizedActions,
      packetId,
      eJournalPacketId,
      auditActionsLoaded = false,
      defaultFee = 0,
    }: ToSealsParams,
  ): EtJournalESeal {
    const holderEntry =
      groupEntries.find(
        (e) => this.findSignerIdForEntry(e, signers) === null,
      ) ?? undefined;
    const signerEntries = groupEntries.filter(
      (e) => this.findSignerIdForEntry(e, signers) !== null,
    );
    const sortedSignerEntries = [...signerEntries].sort((a, b) =>
      a.eJournalEntryId.localeCompare(b.eJournalEntryId),
    );

    const metaSource = holderEntry ?? sortedSignerEntries[0];
    const dateKey = this.normalizeNotarizationDate(
      metaSource?.notarizationDate,
    );
    const signerIds = sortedSignerEntries
      .map((e) => this.findSignerIdForEntry(e, signers))
      .filter((id): id is string => id !== null);
    const uniqueSignerIds = [...new Set(signerIds)];

    const matchingAudit = notarizedActions.find(
      (a) =>
        a.actionType === AuditActionType.Notarized &&
        this.normalizeNotarizationDate(a.actionDateTime) === dateKey,
    );
    const persistedType = metaSource?.entryType;
    let source: EtJournalSealSource;
    if (persistedType === 'seal') {
      source = 'seal';
    } else if (persistedType === 'eSeal' || persistedType === 'deletedESeal') {
      // eSeal-origin: only degrade to deletedESeal when we KNOW audits loaded and none match
      source = auditActionsLoaded && !matchingAudit ? 'deletedESeal' : 'eSeal';
    } else {
      source = matchingAudit ? 'eSeal' : 'seal';
    }
    const sealId =
      holderEntry?.eJournalEntryId ??
      sortedSignerEntries[0]?.eJournalEntryId ??
      dateKey;

    return {
      sealId,
      source,
      documentDescription: metaSource?.documentDescription ?? '',
      pageNumber: metaSource?.pageNumber,
      notorialAct: metaSource?.notorialAct ?? '',
      notarizationDate: dateKey,
      feeCharged: metaSource?.feeCharged ?? defaultFee,
      notes: metaSource?.notes,
      utcCreatedOn: metaSource?.utcCreatedOn ?? dateKey,
      ipAddress: metaSource?.ipAddress,
      eJournalPacketId: metaSource?.eJournalPacketId || eJournalPacketId,
      packetId: metaSource?.packetId || packetId,
      holderEntry,
      entries: sortedSignerEntries,
      signerIds: uniqueSignerIds,
      auditActionId: matchingAudit?.actionId ?? matchingAudit?.id,
    };
  }

  private auditActionToVirtualSeal(
    action: AuditAction,
    dateKey: string,
    {
      packetId,
      eJournalPacketId,
      pageNamesByNumber = {},
      defaultFee = 0,
    }: ToSealsParams,
  ): EtJournalESeal {
    const detail = this.parseNotarizedDetail(action);
    const notorialAct =
      detail?.['notarial-act'] != null ? String(detail['notarial-act']) : '';
    const auditActionId = action.actionId ?? action.id;
    const pageNumber = detail?.page;
    const documentDescription =
      pageNumber != null ? pageNamesByNumber[pageNumber] ?? '' : '';

    return {
      sealId: auditActionId ? String(auditActionId) : dateKey,
      source: 'eSeal',
      documentDescription,
      pageNumber,
      notorialAct,
      notarizationDate: dateKey,
      feeCharged: defaultFee,
      notes: undefined,
      utcCreatedOn: dateKey,
      ipAddress: undefined,
      eJournalPacketId,
      packetId,
      holderEntry: undefined,
      entries: [],
      signerIds: [],
      auditActionId,
    };
  }

  private mapEntriesToSigners(
    entries: EtJournalEntry[],
    signers: EtJournalSigner[],
  ): Map<string, EtJournalEntry> {
    const map = new Map<string, EtJournalEntry>();
    for (const entry of entries) {
      const signerId = this.findSignerIdForEntry(entry, signers);
      if (signerId) {
        map.set(signerId, entry);
      }
    }
    return map;
  }

  private sealMetadataChanged(
    previous: EtJournalESeal,
    next: EtJournalESeal,
  ): boolean {
    return (
      previous.documentDescription !== next.documentDescription ||
      previous.pageNumber !== next.pageNumber ||
      previous.notorialAct !== next.notorialAct ||
      previous.feeCharged !== next.feeCharged ||
      this.normalizeNotarizationDate(previous.notarizationDate) !==
        this.normalizeNotarizationDate(next.notarizationDate) ||
      (previous.notes ?? '') !== (next.notes ?? '')
    );
  }

  private parseEntryIds(raw: string): string[] {
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
}
